import React, { useMemo } from "react"
import { useMatches, useKBar, KBarResults } from "kbar"
import {
  ResultsContainer,
  ResultItem,
  ResultName,
  ResultSubtitle,
  SectionHeader,
  EmptyStateHint,
  CurrentBadge,
} from "../styles"
import { highlightMatches } from "../utils/highlighting"
import { getCurrentRepo } from "../utils/repo"
import { RandomDocSuggestions } from "./RandomDocSuggestions"
import { useAlgoliaSearching } from "./AlgoliaSearchIntegration"
import { MIN_SEARCH_LENGTH } from "../constants"

export function RenderResults() {
  const { results } = useMatches()
  const { searchQuery } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }))
  const isSearching = useAlgoliaSearching()

  const showEmptyHint = !searchQuery || searchQuery.length < MIN_SEARCH_LENGTH
  const hasSearchQuery = searchQuery && searchQuery.length >= MIN_SEARCH_LENGTH
  const hasNoResults = hasSearchQuery && results.length === 0 && !isSearching

  const hasRecentDocs = results.some(
    (item) => typeof item !== "string" && item.section === "Recently viewed"
  )

  // Calculate result counts per section
  const sectionCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    let currentSection = ""

    results.forEach((item) => {
      if (typeof item === "string") {
        currentSection = item
        counts[currentSection] = 0
      } else if (currentSection) {
        counts[currentSection] = (counts[currentSection] || 0) + 1
      }
    })

    return counts
  }, [results])

  const currentPathSet = useMemo(() => {
    const set = new Set<string>()
    results.forEach((item) => {
      if (typeof item !== "string" && item.section === "Recently viewed") {
        const pathname =
          typeof window !== "undefined" ? window.location.pathname : null
        if (pathname && item.subtitle === pathname) {
          set.add(item.id)
        }
      }
    })
    return set
  }, [results])

  const currentRepo = getCurrentRepo()

  return (
    <>
      {showEmptyHint && (
        <EmptyStateHint>
          {hasRecentDocs ? (
            <>
              <span role="img" aria-label="hint">
                💡
              </span>{" "}
              Quick access to sections and recent pages. Start typing to search
              all documentation.
            </>
          ) : (
            <>
              <span role="img" aria-label="hint">
                💡
              </span>{" "}
              Start typing to search documentation. Visit more pages to see
              recent history here.
            </>
          )}
        </EmptyStateHint>
      )}
      {hasNoResults && (
        <>
          <EmptyStateHint>
            No results for "<strong>{searchQuery}</strong>"
          </EmptyStateHint>
          {currentRepo && <RandomDocSuggestions indexName={currentRepo} />}
        </>
      )}
      <ResultsContainer role="listbox" aria-label="Search results">
        <KBarResults
          items={results}
          onRender={({ item, active }) => {
            if (typeof item === "string") {
              const count = sectionCounts[item] || 0
              return (
                <SectionHeader role="presentation">
                  {item}
                  {count > 0 && <span style={{ fontWeight: 400 }}> ({count})</span>}
                </SectionHeader>
              )
            }

            return (
              <ResultItem
                active={active}
                role="option"
                aria-selected={active}
                section={item.section}
              >
                <ResultName>
                  <span>{highlightMatches(item.name, searchQuery)}</span>
                  {currentPathSet.has(item.id) && (
                    <CurrentBadge>[current]</CurrentBadge>
                  )}
                </ResultName>
                {item.subtitle && (
                  <ResultSubtitle>{item.subtitle}</ResultSubtitle>
                )}
              </ResultItem>
            )
          }}
        />
      </ResultsContainer>
    </>
  )
}
